import React, { useCallback, useRef, useState } from 'react';
import { useLatest, useLoad } from 'shared/hooks';
import axios from 'axios';
import { Alert, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { config } from 'shared/config';
import { sourceIdentityChanged } from 'shared/api/sourceAuthRecovery';
import type { ConnectionDetails } from './model/types';

const AUTH = `${config.API_URL}/api/connections/browser-auth`;
export const BROWSER_AUTH_METHODS = ['EntraBrowser', 'DatabricksEntraBrowser', 'DatabricksOAuth', 'SnowflakeOAuth'];
type Identity = { state?: string; identityKey?: string; hasIdentity?: boolean; checkedAt?: string; supported: boolean; configured: boolean; connected: boolean; method?: string; accountName?: string; message?: string; revision?: string };
const message = (e: any) => e?.response?.data?.message || e?.message || 'Sign-in could not be completed.';
type Props = { details?: Partial<ConnectionDetails>; connectionId?: number; reportId?: string; onConnected?: () => void; onDisconnected?: () => void; onSignedInChange?: (signedIn: boolean) => void };

export default function SourceBrowserSignIn({ details, connectionId, reportId, onConnected, onDisconnected, onSignedInChange }: Props) {
    const aas = details?.authType === 'AASInteractive';
    const auth = aas ? `${config.API_URL}/api/connections/analysis-services/auth` : AUTH;
    // Analysis Services sign-in names the server; Unity looks up its Microsoft directory (a saved tenant still wins).
    const authBody = () => aas ? { serverName: details?.serverName, tenantId: details?.analysisServicesOptions?.tenantId } : JSON.parse(body);
    const body = JSON.stringify(connectionId ? { connectionId, reportId } : details);
    const latest = useRef(body); latest.current = body;
    const cleanup = useRef<(() => void) | null>(null);
    const [identityResponse, setIdentityResponse] = useState<{ body: string; identity: Identity } | null>(null);
    const identity = identityResponse?.body === body ? identityResponse.identity : null;
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [url, setUrl] = useState('');
    // The parent hears whether the source is signed in, and not in the middle of signing in, from the
    // places that change either. (It keys what it hears to the details it showed.)
    const onSignedInChangeRef = useLatest(onSignedInChange);
    const signedInNow = useRef<{ identity: Identity | null; busy: boolean }>({ identity: null, busy: false });
    const tellSignedIn = useCallback((change: Partial<{ identity: Identity | null; busy: boolean }>) => {
        signedInNow.current = { ...signedInNow.current, ...change };
        onSignedInChangeRef.current?.(signedInNow.current.identity?.connected === true && !signedInNow.current.busy);
    }, [onSignedInChangeRef]);
    const showIdentity = useCallback((next: { body: string; identity: Identity } | null) => {
        setIdentityResponse(next);
        tellSignedIn({ identity: next?.identity ?? null });
    }, [tellSignedIn]);
    const showBusy = (next: boolean) => {
        setBusy(next);
        tellSignedIn({ busy: next });
    };
    const refresh = useCallback(async () => {
        if (latest.current === body) showIdentity(null);
        const { data } = await axios.post<Identity>(`${AUTH}/status`, JSON.parse(body));
        if (latest.current === body) showIdentity({ body, identity: data });
        return data;
    }, [body, showIdentity]);
    // The sign-in state is read again, a moment after the details settle.
    useLoad(signal => {
        showIdentity(null); setError('');
        const timer = window.setTimeout(() => { void refresh().catch(e => { if (latest.current === body) setError(message(e)); }); }, 300);
        signal.addEventListener('abort', () => { window.clearTimeout(timer); cleanup.current?.(); });
    }, [showIdentity, body, refresh]);
    const signIn = async () => {
        const popup = window.open('about:blank', 'unity-source-sign-in', 'width=600,height=760');
        showBusy(true); setError('');
        let attempt: string | undefined;
        try {
            const { data } = await axios.post(`${auth}/start`, authBody());
            attempt = data.attempt;
            if (latest.current !== body) throw new Error('Connection settings changed. Start sign-in again.');
            const destination = new URL(data.authorizationUrl);
            if (destination.protocol !== 'https:') throw new Error('Invalid sign-in address.');
            setUrl(destination.href);
            await new Promise<void>((resolve, reject) => {
                let finished = false, checking = false;
                const finish = (failure?: Error) => {
                    if (finished) return;
                    finished = true;
                    window.clearInterval(poll); window.clearTimeout(timer);
                    window.removeEventListener('message', listener); cleanup.current = null;
                    failure ? reject(failure) : resolve();
                };
                const listener = (event: MessageEvent) => {
                    if (!popup || event.source !== popup || event.origin !== new URL(config.API_URL, window.location.origin).origin || event.data?.type !== (aas ? 'unity-aas-auth' : 'unity-source-auth')) return;
                    finish(event.data.ok ? undefined : new Error('Sign-in was declined or could not finish. Check the provider setup and try again.'));
                };
                const timer = window.setTimeout(() => finish(new Error('Sign-in timed out. Try again.')), 600000);
                const poll = window.setInterval(async () => {
                    if (checking || finished) return;
                    checking = true;
                    try {
                        const next = await refresh();
                        if (next.connected && next.revision && next.revision !== data.previousRevision) finish();
                    } catch { /* Retry transient status failures until the deadline. */ }
                    finally { checking = false; }
                }, 2000);
                cleanup.current = () => { popup?.close(); finish(new Error('Sign-in cancelled.')); };
                window.addEventListener('message', listener);
                if (popup && !popup.closed) popup.location.href = destination.href;
            });
            const next = await refresh();
            if (!next.connected) throw new Error(next.message || 'Sign-in still needs attention.');
            sourceIdentityChanged(next.identityKey); onConnected?.();
        } catch (e) {
            if (attempt) await axios.post(`${auth}/cancel`, { attempt }).catch(() => undefined);
            if (latest.current === body) setError(message(e));
            popup?.close();
        } finally { showBusy(false); setUrl(''); }
    };
    if (identity?.supported === false) return null;
    const method = identity?.method || details?.authType || '';
    const provider = method === 'AASInteractive' || method.startsWith('Entra') || method === 'DatabricksEntraBrowser' ? 'Microsoft' : method === 'SnowflakeOAuth' ? 'Snowflake' : method === 'DatabricksOAuth' ? 'Databricks' : '';
    return <Stack spacing={1} sx={{ my: 1 }}>
        {identity?.connected && <Typography variant="body2" color="success.main">Signed in as {identity.accountName || `your ${provider} account`}</Typography>}
        {identity && !identity.connected && <Typography variant="body2" color="text.secondary">
            Not signed in. {identity.state === 'setup_required' ? `${provider || 'Provider'} sign-in is currently unavailable.` : 'Please sign in.'}
        </Typography>}
        {identity?.message && identity.state !== 'sign_in_required' && identity.state !== 'setup_required' &&
            <Alert severity={identity.state === 'unavailable' ? 'info' : 'warning'}>{identity.message}</Alert>}
        {!identity && !error && <Typography variant="body2" color="text.secondary">Checking sign-in...</Typography>}
        {error && <Alert severity="error">{error}</Alert>}
        <Stack direction="row" spacing={1} flexWrap="wrap">
            <Button size="small" variant="outlined" disabled={busy || !identity?.configured} onClick={() => identity?.state === 'unavailable' ? void refresh().catch(e => setError(message(e))) : void signIn()}>
                {busy ? <CircularProgress size={18} /> : identity?.state === 'unavailable' ? 'Retry check' : identity?.connected ? 'Change account' : identity?.hasIdentity ? `Reconnect ${provider}` : provider ? `Sign in with ${provider}` : 'Sign in'}
            </Button>
            {identity?.hasIdentity && <Button size="small" disabled={busy} onClick={() => {
                showBusy(true); void axios.post(`${auth}/disconnect`, authBody()).then(refresh).then(() => { sourceIdentityChanged(identity?.identityKey, false); onDisconnected?.(); })
                    .catch(e => setError(message(e))).finally(() => showBusy(false));
            }}>Disconnect my account</Button>}
        </Stack>
        <Button size="small" sx={{ alignSelf: 'flex-start' }} disabled={busy} onClick={() => void refresh().catch(e => setError(message(e)))}>Check sign-in</Button>
        {busy && url && <Stack direction="row" spacing={1}>
            <Button component="a" href={url} target="_blank" rel="noopener noreferrer" size="small">Open sign-in</Button>
            <Button size="small" onClick={() => cleanup.current?.()}>Cancel sign-in</Button>
        </Stack>}
    </Stack>;
}
